import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import soundtrackSrc from "./assets/soundtrack.mp3";

// Grid dimensions in cells
const GRID_W = 200;
const GRID_H = 150;
// Each cell is 1 world unit
const CELL_SIZE = 1;
// Percentage of territory needed to complete a level
const WIN_PERCENT = 75;
// Maximum level in the game
const MAX_LEVEL = 99;
// Cell states
const CELL_EMPTY = 0;
const CELL_CAPTURED = 1;
const CELL_TRAIL = 2;

// Colors used throughout the game
const COLOR_BG = 0x0a0a2a;
const COLOR_CAPTURED = { r: 0, g: 204, b: 204, a: 153 }; // teal with alpha
const COLOR_TRAIL = { r: 255, g: 204, b: 0, a: 255 }; // bright yellow/gold
const COLOR_BORDER_LINE = 0x00ffff;
const COLOR_PLAYER = 0x00ff66;
const COLOR_EMPTY = { r: 10, g: 10, b: 42, a: 255 };

// Palette of fill colors for captured territory — synthwave 80s neon/fluo with glow transparency
const CAPTURE_COLORS = [
  { r: 0, g: 255, b: 255, a: 140 },     // electric cyan (border/initial)
  { r: 255, g: 0, b: 153, a: 160 },     // neon hot pink
  { r: 153, g: 51, b: 255, a: 150 },    // deep neon purple
  { r: 0, g: 255, b: 136, a: 140 },     // laser green
  { r: 255, g: 102, b: 0, a: 155 },     // sunset orange
  { r: 255, g: 0, b: 255, a: 150 },     // vivid magenta/fuchsia
  { r: 51, g: 153, b: 255, a: 145 },    // retro electric blue
  { r: 255, g: 255, b: 0, a: 130 },     // neon yellow
  { r: 255, g: 51, b: 102, a: 155 },    // neon rose
  { r: 0, g: 204, b: 255, a: 140 },     // synthwave sky blue
  { r: 204, g: 0, b: 255, a: 150 },     // ultraviolet
  { r: 57, g: 255, b: 20, a: 135 },     // radioactive green
];

/**
 * DPadButton - A single directional button for mobile touch controls.
 * Uses pointer events to set/unset key state in the shared keysRef.
 */
function DPadButton({ label, keyCode, keysRef }) {
  // Handle pointer down: mark the key as pressed
  const press = useCallback((e) => {
    e.preventDefault();
    keysRef.current[keyCode] = true;
  }, [keyCode, keysRef]);

  // Handle pointer up/leave/cancel: mark the key as released
  const release = useCallback((e) => {
    e.preventDefault();
    keysRef.current[keyCode] = false;
  }, [keyCode, keysRef]);

  return (
    <div
      onPointerDown={press}
      onPointerUp={release}
      onPointerLeave={release}
      onPointerCancel={release}
      style={{
        width: 58, height: 58,
        background: "rgba(0, 40, 80, 0.8)",
        border: "2px solid #00cccc",
        borderRadius: 10,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 24, color: "#00ffcc",
        userSelect: "none", cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        touchAction: "none",
        boxShadow: "0 0 14px #00888855, inset 0 1px 0 rgba(255,255,255,0.1)",
        transition: "background 0.08s, transform 0.08s",
        fontFamily: "monospace",
      }}
    >
      {label}
    </div>
  );
}

/**
 * Creates a fresh 200x150 grid with borders captured and everything else empty.
 * Returns a 2D array [y][x] where border cells are CELL_CAPTURED and interior cells are CELL_EMPTY.
 */
function createGrid() {
  const grid = [];
  for (let y = 0; y < GRID_H; y++) {
    grid[y] = new Uint8Array(GRID_W);
    for (let x = 0; x < GRID_W; x++) {
      if (x === 0 || x === GRID_W - 1 || y === 0 || y === GRID_H - 1) {
        grid[y][x] = CELL_CAPTURED;
      } else {
        grid[y][x] = CELL_EMPTY;
      }
    }
  }
  return grid;
}

/**
 * Calculates the percentage of the total grid area that has been captured.
 * Only counts interior cells (non-border) for the total.
 */
function calcCapturedPercent(grid) {
  let captured = 0;
  let total = 0;
  for (let y = 0; y < GRID_H; y++) {
    for (let x = 0; x < GRID_W; x++) {
      total++;
      if (grid[y][x] === CELL_CAPTURED) {
        captured++;
      }
    }
  }
  return (captured / total) * 100;
}

/**
 * Territory capture algorithm using flood fill.
 * 1. Marks all trail cells as captured.
 * 2. Flood-fills from enemy positions to find all uncaptured cells reachable by enemies.
 * 3. Any unreachable empty cells become captured (the smaller region without the enemy).
 * Returns the new captured percentage.
 */
function captureTerritory(grid, enemies, colorMap, colorIndex) {
  // Step 1: Mark all trail cells as captured and assign the current color index
  for (let y = 0; y < GRID_H; y++) {
    for (let x = 0; x < GRID_W; x++) {
      if (grid[y][x] === CELL_TRAIL) {
        grid[y][x] = CELL_CAPTURED;
        colorMap[y][x] = colorIndex;
      }
    }
  }

  // Step 2: Flood-fill from each enemy position through empty cells
  const visited = new Uint8Array(GRID_W * GRID_H);
  const queue = [];

  // Seed the BFS from every enemy position
  for (const enemy of enemies) {
    const ex = Math.floor(enemy.x);
    const ey = Math.floor(enemy.y);
    // Clamp to grid bounds
    const cx = Math.max(1, Math.min(GRID_W - 2, ex));
    const cy = Math.max(1, Math.min(GRID_H - 2, ey));
    const idx = cy * GRID_W + cx;
    if (grid[cy][cx] === CELL_EMPTY && !visited[idx]) {
      visited[idx] = 1;
      queue.push(cx, cy);
    }
    // Also seed from neighboring cells to handle edge cases
    for (const [dx, dy] of [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx >= 0 && nx < GRID_W && ny >= 0 && ny < GRID_H) {
        const ni = ny * GRID_W + nx;
        if (grid[ny][nx] === CELL_EMPTY && !visited[ni]) {
          visited[ni] = 1;
          queue.push(nx, ny);
        }
      }
    }
  }

  // BFS through all empty cells reachable from enemy positions
  let head = 0;
  while (head < queue.length) {
    const qx = queue[head++];
    const qy = queue[head++];
    const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    for (const [dx, dy] of dirs) {
      const nx = qx + dx;
      const ny = qy + dy;
      if (nx >= 0 && nx < GRID_W && ny >= 0 && ny < GRID_H) {
        const ni = ny * GRID_W + nx;
        if (grid[ny][nx] === CELL_EMPTY && !visited[ni]) {
          visited[ni] = 1;
          queue.push(nx, ny);
        }
      }
    }
  }

  // Step 3: Any empty cell NOT visited by the flood fill becomes captured, with current color
  for (let y = 0; y < GRID_H; y++) {
    for (let x = 0; x < GRID_W; x++) {
      if (grid[y][x] === CELL_EMPTY && !visited[y * GRID_W + x]) {
        grid[y][x] = CELL_CAPTURED;
        colorMap[y][x] = colorIndex;
      }
    }
  }

  return calcCapturedPercent(grid);
}

/**
 * Checks if a grid cell is on the boundary of captured territory
 * (i.e., it is captured and has at least one uncaptured neighbor).
 */
function isBorderCell(grid, x, y) {
  if (grid[y][x] !== CELL_CAPTURED) return false;
  const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];
  for (const [dx, dy] of dirs) {
    const nx = x + dx;
    const ny = y + dy;
    if (nx >= 0 && nx < GRID_W && ny >= 0 && ny < GRID_H) {
      if (grid[ny][nx] === CELL_EMPTY) return true;
    }
  }
  // Also true if on the actual grid edge
  if (x === 0 || x === GRID_W - 1 || y === 0 || y === GRID_H - 1) return true;
  return false;
}

/**
 * Determines the next cell for a Sparx to move to using wall-following algorithm.
 * Clockwise sparx uses right-hand rule, counterclockwise uses left-hand rule.
 * Priority: preferred turn → straight → opposite turn → reverse.
 * Returns {x, y, dx, dy} for the next position and direction.
 */
function getNextSparxCell(grid, x, y, dx, dy, clockwise) {
  // Right turn: (-dy, dx), Left turn: (dy, -dx)
  const turns = clockwise
    ? [
        { dx: -dy, dy: dx },    // right turn (preferred for clockwise)
        { dx, dy },              // straight
        { dx: dy, dy: -dx },    // left turn
        { dx: -dx, dy: -dy },   // reverse (last resort)
      ]
    : [
        { dx: dy, dy: -dx },    // left turn (preferred for counter-clockwise)
        { dx, dy },              // straight
        { dx: -dy, dy: dx },    // right turn
        { dx: -dx, dy: -dy },   // reverse (last resort)
      ];

  for (const dir of turns) {
    const nx = x + dir.dx;
    const ny = y + dir.dy;
    if (nx >= 0 && nx < GRID_W && ny >= 0 && ny < GRID_H) {
      if (isBorderCell(grid, nx, ny)) {
        return { x: nx, y: ny, dx: dir.dx, dy: dir.dy };
      }
    }
  }
  // Fallback: stay in place (should not happen on a closed border loop)
  return { x, y, dx, dy };
}

/**
 * Relocates a Sparx to the nearest valid border cell if its current position
 * is no longer on the border (e.g., after territory capture changed the topology).
 * Uses an expanding search radius from the sparx's current position.
 */
function relocateSparxToBorder(grid, sparx) {
  if (isBorderCell(grid, sparx.x, sparx.y)) return;
  const maxR = Math.max(GRID_W, GRID_H);
  for (let r = 1; r < maxR; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        const nx = sparx.x + dx;
        const ny = sparx.y + dy;
        if (nx >= 0 && nx < GRID_W && ny >= 0 && ny < GRID_H) {
          if (isBorderCell(grid, nx, ny)) {
            sparx.x = nx;
            sparx.y = ny;
            return;
          }
        }
      }
    }
  }
}

/**
 * Updates the DataTexture pixel data from the grid state.
 * Captured cells get teal color, trail cells get yellow, empty cells get dark background.
 */
function updateTextureFromGrid(data, grid, colorMap) {
  for (let y = 0; y < GRID_H; y++) {
    for (let x = 0; x < GRID_W; x++) {
      // DataTexture is bottom-up by default, flip y
      const texY = GRID_H - 1 - y;
      const idx = (texY * GRID_W + x) * 4;
      const cell = grid[y][x];
      if (cell === CELL_CAPTURED) {
        // Look up the capture color from the palette using the cell's color index
        const color = CAPTURE_COLORS[colorMap[y][x] % CAPTURE_COLORS.length];
        data[idx] = color.r;
        data[idx + 1] = color.g;
        data[idx + 2] = color.b;
        data[idx + 3] = color.a;
      } else if (cell === CELL_TRAIL) {
        data[idx] = COLOR_TRAIL.r;
        data[idx + 1] = COLOR_TRAIL.g;
        data[idx + 2] = COLOR_TRAIL.b;
        data[idx + 3] = COLOR_TRAIL.a;
      } else {
        data[idx] = COLOR_EMPTY.r;
        data[idx + 1] = COLOR_EMPTY.g;
        data[idx + 2] = COLOR_EMPTY.b;
        data[idx + 3] = COLOR_EMPTY.a;
      }
    }
  }
}

/**
 * StyxxerGame - The main game component implementing a Qix-style territory capture game.
 * Uses Three.js for rendering with an OrthographicCamera for 2D top-down view.
 * All game state is managed via refs for performance, with React state for HUD display.
 */
export default function StyxxerGame() {
  // Ref for the Three.js mount div
  const mountRef = useRef(null);
  // Ref for all mutable game state (grid, player, enemies, etc.)
  const stateRef = useRef(null);
  // Ref for tracking which keys are currently pressed
  const keysRef = useRef({});
  // Ref for the requestAnimationFrame ID so we can cancel it
  const animFrameRef = useRef(null);
  // Ref for the background soundtrack HTML Audio element
  const audioRef = useRef(null);

  // React state for HUD display values
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [capturedPercent, setCapturedPercent] = useState(0);
  // Game state machine: 'menu' | 'playing' | 'levelComplete' | 'gameOver' | 'won'
  const [gameState, setGameState] = useState("menu");
  // Whether to show on-screen mobile D-pad controls
  const [showControls, setShowControls] = useState(true);
  // Whether to show the "You're dead!" overlay after the player loses a life
  const [showDeathOverlay, setShowDeathOverlay] = useState(false);
  // Whether the background soundtrack is muted
  const [isMuted, setIsMuted] = useState(false);

  /**
   * Initializes or resets the game for a given level.
   * Creates the grid, positions the player, spawns enemies.
   */
  const initLevel = useCallback((levelNum) => {
    const grid = createGrid();
    const trail = [];
    const playerX = Math.floor(GRID_W / 2);
    const playerY = 0;
    const isDrawing = false;
    // Player direction: 0=none, or {dx, dy}
    const playerDir = { dx: 0, dy: 0 };
    // Last direction the player moved in while drawing, used to prevent reversals
    const lastTrailDir = { dx: 0, dy: 0 };
    // Last safe position (on border/captured edge) for respawn
    const lastSafePos = { x: playerX, y: playerY };

    // Enemy base speed scales aggressively with level for increasing difficulty
    const baseSpeed = 18;
    const enemySpeed = baseSpeed * (1 + levelNum * 0.15);

    // Direction change frequency increases with level (shorter timers = more erratic)
    const minDirChangeTime = Math.max(0.08, 0.5 - levelNum * 0.04);
    const maxDirChangeTime = Math.max(0.2, 1.5 - levelNum * 0.08);

    // Dash mechanic: enemy periodically bursts at high speed for a short duration
    const dashChance = Math.min(0.6, 0.05 + levelNum * 0.04); // probability per direction change
    const dashSpeedMultiplier = 1.8 + levelNum * 0.1; // how much faster the dash is
    const dashDuration = Math.min(0.5, 0.15 + levelNum * 0.01); // how long the dash lasts (seconds)

    // Jitter intensity: small random perturbations added to movement each frame
    const jitterIntensity = Math.min(8, levelNum * 0.4);

    // Spawn the Styxxer enemy at a random position in uncaptured territory
    const spawnEnemy = () => {
      let ex, ey;
      let attempts = 0;
      do {
        ex = Math.floor(Math.random() * (GRID_W - 40)) + 20;
        ey = Math.floor(Math.random() * (GRID_H - 40)) + 20;
        attempts++;
      } while (grid[ey][ex] !== CELL_EMPTY && attempts < 1000);
      return {
        x: ex + 0.5, y: ey + 0.5,
        dx: (Math.random() - 0.5) * 2, dy: (Math.random() - 0.5) * 2,
        dirChangeTimer: 1.0,
        isDashing: false,   // whether the enemy is currently in a dash burst
        dashTimer: 0,       // remaining dash duration
        currentSpeedMult: 1, // current speed multiplier (1 = normal, higher during dash)
      };
    };

    const enemy = spawnEnemy();
    // Normalize enemy direction
    const len = Math.sqrt(enemy.dx * enemy.dx + enemy.dy * enemy.dy);
    if (len > 0) { enemy.dx /= len; enemy.dy /= len; }

    // Create a color map to track which capture color each cell belongs to
    // Border cells default to index 0 (first palette color)
    const colorMap = [];
    for (let y = 0; y < GRID_H; y++) {
      colorMap[y] = new Uint8Array(GRID_W);
    }

    const pct = calcCapturedPercent(grid);

    stateRef.current = {
      grid,
      colorMap,
      trail,
      player: { x: playerX, y: playerY },
      playerDir,
      lastTrailDir,
      lastSafePos,
      isDrawing,
      enemy,
      enemySpeed,
      // Level-scaling enemy behavior parameters
      minDirChangeTime,
      maxDirChangeTime,
      dashChance,
      dashSpeedMultiplier,
      dashDuration,
      jitterIntensity,
      levelNum,
      lives: stateRef.current?.lives ?? 3,
      capturedPct: pct,
      captureColorIndex: 0, // index into CAPTURE_COLORS palette, incremented on each capture
      gridDirty: true, // flag to update the DataTexture
      moveAccum: 0, // accumulator for delta-time-based movement
      drawMoveAccum: 0, // accumulator while drawing
      time: 0,
      // Sparx: border-patrol enemies, count scales with level
      // Level 1-2: 1, Level 3-7: 2, Level 8-12: 3, Level 13-20: 4, Level 21+: 5
      sparxList: (() => {
        const sparxCount = levelNum <= 2 ? 1 : levelNum <= 7 ? 2 : levelNum <= 12 ? 3 : levelNum <= 20 ? 4 : 5;
        // Predefined spawn positions spread around the border with alternating directions
        const sparxConfigs = [
          { x: 1, y: 0, dx: 1, dy: 0, clockwise: true },
          { x: GRID_W - 2, y: GRID_H - 1, dx: -1, dy: 0, clockwise: false },
          { x: GRID_W - 1, y: 1, dx: 0, dy: 1, clockwise: true },
          { x: 0, y: GRID_H - 2, dx: 0, dy: -1, clockwise: false },
          { x: Math.floor(GRID_W / 2), y: GRID_H - 1, dx: -1, dy: 0, clockwise: true },
        ];
        return sparxConfigs.slice(0, sparxCount).map(c => ({ ...c, moveAccum: 0 }));
      })(),
      sparxSpeed: Math.min(45, 18 + levelNum * 1.5), // cells per second, scales with level
      invincibleTimer: 0, // brief invincibility period after losing a life
    };

    setCapturedPercent(Math.floor(pct));
  }, []);

  /**
   * Main useEffect that sets up Three.js, the game loop, input handlers, and cleanup.
   * Only runs when gameState is 'playing'.
   */
  useEffect(() => {
    if (gameState !== "playing") return;

    const container = mountRef.current;
    if (!container) return;
    const W = container.clientWidth;
    const H = container.clientHeight;

    // Create WebGL renderer
    const renderer = new THREE.WebGLRenderer({ antialias: false });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(COLOR_BG);
    container.appendChild(renderer.domElement);

    // Create orthographic camera for 2D top-down view
    const aspect = W / H;
    const viewHeight = GRID_H * CELL_SIZE * 1.15;
    const viewWidth = viewHeight * aspect;
    const camera = new THREE.OrthographicCamera(
      -viewWidth / 2, viewWidth / 2,
      viewHeight / 2, -viewHeight / 2,
      0.1, 1000
    );
    camera.position.set(GRID_W * CELL_SIZE / 2, 100, GRID_H * CELL_SIZE / 2);
    camera.lookAt(GRID_W * CELL_SIZE / 2, 0, GRID_H * CELL_SIZE / 2);

    // Create scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(COLOR_BG);

    // Add ambient light for consistent illumination
    scene.add(new THREE.AmbientLight(0xffffff, 1.0));

    // --- Create the playfield DataTexture ---
    // This texture maps the 200x150 grid onto a plane, updated when the grid changes
    const texData = new Uint8Array(GRID_W * GRID_H * 4);
    const gridTexture = new THREE.DataTexture(texData, GRID_W, GRID_H, THREE.RGBAFormat);
    gridTexture.magFilter = THREE.NearestFilter;
    gridTexture.minFilter = THREE.NearestFilter;
    gridTexture.needsUpdate = true;

    // Create the playfield plane mesh covering the entire grid area
    const fieldGeo = new THREE.PlaneGeometry(GRID_W * CELL_SIZE, GRID_H * CELL_SIZE);
    const fieldMat = new THREE.MeshBasicMaterial({ map: gridTexture, transparent: true });
    const fieldMesh = new THREE.Mesh(fieldGeo, fieldMat);
    fieldMesh.rotation.x = -Math.PI / 2;
    fieldMesh.position.set(GRID_W * CELL_SIZE / 2, 0, GRID_H * CELL_SIZE / 2);
    scene.add(fieldMesh);

    // --- Create the playfield border rectangle ---
    const borderPoints = [
      new THREE.Vector3(0, 0.1, 0),
      new THREE.Vector3(GRID_W * CELL_SIZE, 0.1, 0),
      new THREE.Vector3(GRID_W * CELL_SIZE, 0.1, GRID_H * CELL_SIZE),
      new THREE.Vector3(0, 0.1, GRID_H * CELL_SIZE),
      new THREE.Vector3(0, 0.1, 0),
    ];
    const borderGeo = new THREE.BufferGeometry().setFromPoints(borderPoints);
    const borderMat = new THREE.LineBasicMaterial({ color: COLOR_BORDER_LINE, linewidth: 2 });
    const borderLine = new THREE.Line(borderGeo, borderMat);
    scene.add(borderLine);

    // --- Create player mesh (bright green glowing square) ---
    const playerGeo = new THREE.BoxGeometry(CELL_SIZE * 1.8, CELL_SIZE * 0.5, CELL_SIZE * 1.8);
    const playerMat = new THREE.MeshBasicMaterial({ color: COLOR_PLAYER });
    const playerMesh = new THREE.Mesh(playerGeo, playerMat);
    playerMesh.position.y = 0.3;
    scene.add(playerMesh);

    // Create a glow effect around the player using a slightly larger transparent mesh
    const glowGeo = new THREE.BoxGeometry(CELL_SIZE * 3, CELL_SIZE * 0.2, CELL_SIZE * 3);
    const glowMat = new THREE.MeshBasicMaterial({ color: COLOR_PLAYER, transparent: true, opacity: 0.3 });
    const glowMesh = new THREE.Mesh(glowGeo, glowMat);
    glowMesh.position.y = 0.2;
    scene.add(glowMesh);

    // --- Create Styxxer enemy mesh (group of animated multicolored line segments) ---
    const styxxerGroup = new THREE.Group();
    const styxxerColors = [0xff00ff, 0x00ffff, 0xffff00, 0xff3333, 0x33ff33, 0xff8800];
    const styxxerLines = [];
    const NUM_SEGMENTS = 8;
    // Create initial line segments for the enemy visual
    for (let i = 0; i < NUM_SEGMENTS; i++) {
      const points = [
        new THREE.Vector3((Math.random() - 0.5) * 6, 0.5, (Math.random() - 0.5) * 6),
        new THREE.Vector3((Math.random() - 0.5) * 6, 0.5, (Math.random() - 0.5) * 6),
      ];
      const lineGeo = new THREE.BufferGeometry().setFromPoints(points);
      const lineMat = new THREE.LineBasicMaterial({
        color: styxxerColors[i % styxxerColors.length],
        linewidth: 2,
      });
      const line = new THREE.Line(lineGeo, lineMat);
      styxxerGroup.add(line);
      styxxerLines.push({ line, geo: lineGeo });
    }
    scene.add(styxxerGroup);

    // --- Create Sparx meshes (small bright cursors that travel along captured borders) ---
    // Pre-allocate the maximum number of meshes (5); unused ones are hidden each frame
    const sparxMeshes = [];
    const MAX_SPARX = 5;
    const SPARX_COLORS = [0xff3300, 0xff0066, 0xffaa00, 0xcc00ff, 0xff6600];
    for (let i = 0; i < MAX_SPARX; i++) {
      // Diamond-shaped body rotated 45 degrees
      const sparxGeo = new THREE.BoxGeometry(CELL_SIZE * 1.5, CELL_SIZE * 0.8, CELL_SIZE * 1.5);
      const sparxMat = new THREE.MeshBasicMaterial({ color: SPARX_COLORS[i] });
      const sparxMesh = new THREE.Mesh(sparxGeo, sparxMat);
      sparxMesh.rotation.y = Math.PI / 4;
      sparxMesh.position.y = 0.4;
      scene.add(sparxMesh);
      // Glow halo around the sparx
      const sparxGlowGeo = new THREE.BoxGeometry(CELL_SIZE * 2.5, CELL_SIZE * 0.2, CELL_SIZE * 2.5);
      const sparxGlowMat = new THREE.MeshBasicMaterial({ color: SPARX_COLORS[i], transparent: true, opacity: 0.3 });
      const sparxGlowMesh = new THREE.Mesh(sparxGlowGeo, sparxGlowMat);
      sparxGlowMesh.rotation.y = Math.PI / 4;
      sparxGlowMesh.position.y = 0.2;
      scene.add(sparxGlowMesh);
      sparxMeshes.push({ mesh: sparxMesh, glow: sparxGlowMesh, glowMat: sparxGlowMat });
    }

    // Initialize game level if not already done
    if (!stateRef.current) {
      initLevel(1);
    }

    // Perform initial texture update
    if (stateRef.current) {
      updateTextureFromGrid(texData, stateRef.current.grid, stateRef.current.colorMap);
      gridTexture.needsUpdate = true;
    }

    // --- Input handling ---
    // Keyboard event handler: track key presses for movement
    const onKeyDown = (e) => {
      keysRef.current[e.code] = true;
      // Prevent default for arrow keys to avoid scrolling
      if (e.code.startsWith("Arrow")) e.preventDefault();
    };
    const onKeyUp = (e) => {
      keysRef.current[e.code] = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    let lastTime = performance.now();

    /**
     * Determines the intended direction from currently pressed keys.
     * Returns {dx, dy} for the direction, or {0,0} if no movement key is pressed.
     */
    const getInputDir = () => {
      const keys = keysRef.current;
      if (keys["ArrowLeft"] || keys["KeyA"]) return { dx: -1, dy: 0 };
      if (keys["ArrowRight"] || keys["KeyD"]) return { dx: 1, dy: 0 };
      if (keys["ArrowUp"] || keys["KeyW"]) return { dx: 0, dy: -1 };
      if (keys["ArrowDown"] || keys["KeyS"]) return { dx: 0, dy: 1 };
      return { dx: 0, dy: 0 };
    };

    /**
     * Checks if a cell is a valid position for the player to move onto captured/border territory.
     * The player can walk on any captured cell.
     */
    const canWalkOnCaptured = (grid, x, y) => {
      if (x < 0 || x >= GRID_W || y < 0 || y >= GRID_H) return false;
      return grid[y][x] === CELL_CAPTURED;
    };

    /**
     * Plays a simple death sound using the Web Audio API.
     * Produces a descending tone burst to signal the player has died.
     */
    const playDeathSound = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        // First tone: high pitch falling
        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.connect(gain1);
        gain1.connect(ctx.destination);
        osc1.type = "sawtooth";
        osc1.frequency.setValueAtTime(880, ctx.currentTime);
        osc1.frequency.exponentialRampToValueAtTime(110, ctx.currentTime + 0.5);
        gain1.gain.setValueAtTime(0.4, ctx.currentTime);
        gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
        osc1.start(ctx.currentTime);
        osc1.stop(ctx.currentTime + 0.5);
        // Second tone: low thud after the fall
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.type = "sine";
        osc2.frequency.setValueAtTime(80, ctx.currentTime + 0.3);
        osc2.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.7);
        gain2.gain.setValueAtTime(0.5, ctx.currentTime + 0.3);
        gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
        osc2.start(ctx.currentTime + 0.3);
        osc2.stop(ctx.currentTime + 0.7);
        // Close context after sounds finish
        setTimeout(() => ctx.close(), 1000);
      } catch (e) {
        // Ignore if Web Audio API is not available
      }
    };

    /**
     * Handles the player losing a life: erases the trail, resets player to last safe position.
     * Shows a "You're dead!" overlay briefly and plays a death sound.
     */
    const handleDeath = (s) => {
      // Erase trail: set all trail cells back to empty
      for (let y = 0; y < GRID_H; y++) {
        for (let x = 0; x < GRID_W; x++) {
          if (s.grid[y][x] === CELL_TRAIL) {
            s.grid[y][x] = CELL_EMPTY;
          }
        }
      }
      s.trail = [];
      s.isDrawing = false;
      s.playerDir = { dx: 0, dy: 0 };
      s.lastTrailDir = { dx: 0, dy: 0 };
      // Return player to last safe border position
      s.player.x = s.lastSafePos.x;
      s.player.y = s.lastSafePos.y;
      s.moveAccum = 0;
      s.drawMoveAccum = 0;
      s.gridDirty = true;
      // Grant brief invincibility after death to prevent instant re-death
      s.invincibleTimer = 2.0;
      // Lose a life
      s.lives--;
      setLives(s.lives);
      // Play death sound and show overlay
      playDeathSound();
      setShowDeathOverlay(true);
      setTimeout(() => setShowDeathOverlay(false), 1500);
      if (s.lives <= 0) {
        setGameState("gameOver");
      }
    };

    /**
     * Animates the Styxxer enemy's line segments by wiggling their vertices randomly.
     * This creates the chaotic multicolored line appearance of the original Qix.
     */
    const animateStyxxer = (time) => {
      for (let i = 0; i < styxxerLines.length; i++) {
        const { geo } = styxxerLines[i];
        const positions = geo.attributes.position.array;
        // Wiggle each vertex slightly using sine/cosine with different phases
        for (let j = 0; j < 2; j++) {
          const offset = j * 3;
          positions[offset] += (Math.sin(time * 3 + i * 1.7 + j * 2.3) * 0.3);
          positions[offset + 2] += (Math.cos(time * 2.5 + i * 1.3 + j * 3.1) * 0.3);
          // Clamp to keep the segments near the center of the enemy group
          positions[offset] = Math.max(-5, Math.min(5, positions[offset]));
          positions[offset + 2] = Math.max(-5, Math.min(5, positions[offset + 2]));
        }
        geo.attributes.position.needsUpdate = true;
      }
      // Rotate line colors over time for visual effect
      const colorTime = Math.floor(time * 4) % styxxerColors.length;
      for (let i = 0; i < styxxerLines.length; i++) {
        styxxerLines[i].line.material.color.setHex(
          styxxerColors[(i + colorTime) % styxxerColors.length]
        );
      }
    };

    /**
     * Main game loop: updates game logic, enemy AI, collision, and renders the scene.
     * Called on each animation frame via requestAnimationFrame.
     */
    const loop = () => {
      animFrameRef.current = requestAnimationFrame(loop);
      const now = performance.now();
      const dt = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;

      const s = stateRef.current;
      if (!s) return;
      s.time = (s.time || 0) + dt;

      // Decrement invincibility timer after death
      if (s.invincibleTimer > 0) {
        s.invincibleTimer = Math.max(0, s.invincibleTimer - dt);
      }

      // --- Player movement ---
      const input = getInputDir();
      const moveSpeed = s.isDrawing ? 28 : 35; // cells per second
      const accumKey = s.isDrawing ? "drawMoveAccum" : "moveAccum";
      s[accumKey] += dt * moveSpeed;

      // Process as many discrete steps as the accumulator allows
      while (s[accumKey] >= 1.0) {
        s[accumKey] -= 1.0;

        // Get the actual direction to try to move
        let dir = { dx: input.dx, dy: input.dy };

        // If no input, keep moving in current direction while drawing
        if (dir.dx === 0 && dir.dy === 0) {
          if (s.isDrawing) {
            dir = { dx: s.playerDir.dx, dy: s.playerDir.dy };
          } else {
            break; // Not drawing and no input — stay put
          }
        }

        // If drawing, prevent reversing onto own trail
        if (s.isDrawing && s.trail.length > 0) {
          const prevTrail = s.trail[s.trail.length - 1];
          const wouldReverse = (s.player.x + dir.dx === prevTrail.x && s.player.y + dir.dy === prevTrail.y);
          if (wouldReverse) {
            // Cannot reverse while drawing — try to continue in current direction
            if (s.playerDir.dx !== 0 || s.playerDir.dy !== 0) {
              dir = { dx: s.playerDir.dx, dy: s.playerDir.dy };
              // Check again for reversal with the fallback direction
              const wouldReverse2 = (s.player.x + dir.dx === prevTrail.x && s.player.y + dir.dy === prevTrail.y);
              if (wouldReverse2) {
                break; // Stuck — cannot move
              }
            } else {
              break;
            }
          }
        }

        const nx = s.player.x + dir.dx;
        const ny = s.player.y + dir.dy;

        // Bounds check
        if (nx < 0 || nx >= GRID_W || ny < 0 || ny >= GRID_H) {
          if (!s.isDrawing) break;
          // While drawing, just stop
          break;
        }

        const targetCell = s.grid[ny][nx];

        if (!s.isDrawing) {
          // Player is on captured territory
          if (targetCell === CELL_CAPTURED) {
            // Move along captured territory
            s.player.x = nx;
            s.player.y = ny;
            s.playerDir = { dx: dir.dx, dy: dir.dy };
            s.lastSafePos = { x: nx, y: ny };
          } else if (targetCell === CELL_EMPTY) {
            // Enter uncaptured territory — start drawing trail
            s.isDrawing = true;
            s.trail = [{ x: s.player.x, y: s.player.y }];
            s.grid[ny][nx] = CELL_TRAIL;
            s.player.x = nx;
            s.player.y = ny;
            s.trail.push({ x: nx, y: ny });
            s.playerDir = { dx: dir.dx, dy: dir.dy };
            s.lastTrailDir = { dx: dir.dx, dy: dir.dy };
            s.gridDirty = true;
          }
        } else {
          // Currently drawing trail
          if (targetCell === CELL_TRAIL) {
            // Would cross own trail — don't allow
            break;
          } else if (targetCell === CELL_CAPTURED) {
            // Trail reconnects to captured territory! Trigger capture!
            s.trail.push({ x: nx, y: ny });
            s.player.x = nx;
            s.player.y = ny;
            s.isDrawing = false;
            s.lastSafePos = { x: nx, y: ny };
            s.playerDir = { dx: 0, dy: 0 };
            s.lastTrailDir = { dx: 0, dy: 0 };

            // Increment capture color index so each polygon gets a unique fill color
            s.captureColorIndex = (s.captureColorIndex + 1) % CAPTURE_COLORS.length;
            // Perform territory capture with the current color
            const newPct = captureTerritory(s.grid, [s.enemy], s.colorMap, s.captureColorIndex);
            s.capturedPct = newPct;
            setCapturedPercent(Math.floor(newPct));
            s.trail = [];
            s.gridDirty = true;

            // Relocate sparx to valid border cells after territory topology change
            for (const sparx of s.sparxList) {
              relocateSparxToBorder(s.grid, sparx);
            }

            // Check for level completion
            if (newPct >= WIN_PERCENT) {
              if (s.levelNum >= MAX_LEVEL) {
                setGameState("won");
              } else {
                setGameState("levelComplete");
              }
              return;
            }
            break;
          } else if (targetCell === CELL_EMPTY) {
            // Continue drawing through empty territory
            s.grid[ny][nx] = CELL_TRAIL;
            s.trail.push({ x: nx, y: ny });
            s.player.x = nx;
            s.player.y = ny;
            s.playerDir = { dx: dir.dx, dy: dir.dy };
            s.lastTrailDir = { dx: dir.dx, dy: dir.dy };
            s.gridDirty = true;
          }
        }
      }

      // --- Enemy AI - The Styxxer (level-scaled behavior) ---
      const en = s.enemy;

      // Update dash timer: if currently dashing, count down
      if (en.isDashing) {
        en.dashTimer -= dt;
        if (en.dashTimer <= 0) {
          // Dash ended, return to normal speed
          en.isDashing = false;
          en.currentSpeedMult = 1;
        }
      }

      // Decrease direction change timer and pick new direction when it expires
      en.dirChangeTimer -= dt;
      if (en.dirChangeTimer <= 0) {
        // Pick a new random direction with full 360-degree range
        const angle = Math.random() * Math.PI * 2;
        en.dx = Math.cos(angle);
        en.dy = Math.sin(angle);
        // Timer scales down with level: more frequent changes at higher levels
        en.dirChangeTimer = s.minDirChangeTime + Math.random() * (s.maxDirChangeTime - s.minDirChangeTime);

        // Roll for a dash burst: enemy surges forward at high speed briefly
        if (!en.isDashing && Math.random() < s.dashChance) {
          en.isDashing = true;
          en.dashTimer = s.dashDuration;
          en.currentSpeedMult = s.dashSpeedMultiplier;
        }
      }

      // Apply per-frame jitter: small random deviation added to direction for erratic feel
      const jitter = s.jitterIntensity;
      const jitterDx = (Math.random() - 0.5) * jitter * dt;
      const jitterDy = (Math.random() - 0.5) * jitter * dt;
      const rawDx = en.dx + jitterDx;
      const rawDy = en.dy + jitterDy;
      // Re-normalize the direction vector after adding jitter
      const jLen = Math.sqrt(rawDx * rawDx + rawDy * rawDy);
      const finalDx = jLen > 0 ? rawDx / jLen : en.dx;
      const finalDy = jLen > 0 ? rawDy / jLen : en.dy;

      // Move the enemy using current speed multiplier (normal or dash)
      const effectiveSpeed = s.enemySpeed * en.currentSpeedMult;
      const eMoveX = finalDx * effectiveSpeed * dt;
      const eMoveY = finalDy * effectiveSpeed * dt;
      let newEX = en.x + eMoveX;
      let newEY = en.y + eMoveY;

      // Bounce off captured territory boundaries
      const checkX = Math.floor(newEX);
      const checkY = Math.floor(newEY);
      let bounced = false;

      // Check horizontal boundary collision
      if (checkX < 1 || checkX >= GRID_W - 1 || (checkX >= 0 && checkX < GRID_W && checkY >= 0 && checkY < GRID_H && s.grid[Math.floor(en.y)][checkX] !== CELL_EMPTY && s.grid[Math.floor(en.y)][checkX] !== CELL_TRAIL)) {
        en.dx = -en.dx;
        newEX = en.x;
        bounced = true;
      }
      // Check vertical boundary collision
      if (checkY < 1 || checkY >= GRID_H - 1 || (checkX >= 0 && checkX < GRID_W && checkY >= 0 && checkY < GRID_H && s.grid[checkY][Math.floor(en.x)] !== CELL_EMPTY && s.grid[checkY][Math.floor(en.x)] !== CELL_TRAIL)) {
        en.dy = -en.dy;
        newEY = en.y;
        bounced = true;
      }

      // If still colliding after bounce, try adjusting more aggressively
      if (!bounced) {
        const finalX = Math.floor(newEX);
        const finalY = Math.floor(newEY);
        if (finalX >= 0 && finalX < GRID_W && finalY >= 0 && finalY < GRID_H) {
          if (s.grid[finalY][finalX] !== CELL_EMPTY && s.grid[finalY][finalX] !== CELL_TRAIL) {
            en.dx = -en.dx;
            en.dy = -en.dy;
            newEX = en.x;
            newEY = en.y;
          }
        }
      }

      // Clamp enemy position within uncaptured area
      en.x = Math.max(1.5, Math.min(GRID_W - 1.5, newEX));
      en.y = Math.max(1.5, Math.min(GRID_H - 1.5, newEY));

      // If the enemy somehow ends up in captured territory, push it to nearby empty space
      const enCellX = Math.floor(en.x);
      const enCellY = Math.floor(en.y);
      if (enCellX >= 0 && enCellX < GRID_W && enCellY >= 0 && enCellY < GRID_H) {
        if (s.grid[enCellY][enCellX] === CELL_CAPTURED) {
          // Search nearby for an empty cell
          let found = false;
          for (let r = 1; r < 30 && !found; r++) {
            for (let dy = -r; dy <= r && !found; dy++) {
              for (let dx = -r; dx <= r && !found; dx++) {
                const sx = enCellX + dx;
                const sy = enCellY + dy;
                if (sx >= 1 && sx < GRID_W - 1 && sy >= 1 && sy < GRID_H - 1) {
                  if (s.grid[sy][sx] === CELL_EMPTY) {
                    en.x = sx + 0.5;
                    en.y = sy + 0.5;
                    found = true;
                  }
                }
              }
            }
          }
        }
      }

      // --- Sparx Movement ---
      // Move each sparx along the captured territory border using wall-following
      for (let si = 0; si < s.sparxList.length; si++) {
        const sparx = s.sparxList[si];
        sparx.moveAccum += dt * s.sparxSpeed;
        while (sparx.moveAccum >= 1.0) {
          sparx.moveAccum -= 1.0;
          const next = getNextSparxCell(s.grid, sparx.x, sparx.y, sparx.dx, sparx.dy, sparx.clockwise);
          sparx.x = next.x;
          sparx.y = next.y;
          sparx.dx = next.dx;
          sparx.dy = next.dy;
        }
      }

      // --- Collision Detection (skipped during invincibility) ---
      if (s.invincibleTimer <= 0) {
        const playerCellX = s.player.x;
        const playerCellY = s.player.y;
        const enemyGridX = Math.floor(en.x);
        const enemyGridY = Math.floor(en.y);

        // Check if Styxxer enemy overlaps with player position
        if (Math.abs(enemyGridX - playerCellX) <= 1 && Math.abs(enemyGridY - playerCellY) <= 1) {
          const dist = Math.sqrt(
            (en.x - (playerCellX + 0.5)) ** 2 + (en.y - (playerCellY + 0.5)) ** 2
          );
          if (dist < 2.0) {
            handleDeath(s);
          }
        }

        // Check if Styxxer enemy overlaps with any trail cell
        if (s.isDrawing) {
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              const cx = enemyGridX + dx;
              const cy = enemyGridY + dy;
              if (cx >= 0 && cx < GRID_W && cy >= 0 && cy < GRID_H) {
                if (s.grid[cy][cx] === CELL_TRAIL) {
                  handleDeath(s);
                  break;
                }
              }
            }
          }
        }

        // Check if any Sparx is on the same cell as the player
        for (const sparx of s.sparxList) {
          if (sparx.x === s.player.x && sparx.y === s.player.y) {
            handleDeath(s);
            break;
          }
        }
      }

      // --- Update DataTexture if grid changed ---
      if (s.gridDirty) {
        updateTextureFromGrid(texData, s.grid, s.colorMap);
        gridTexture.needsUpdate = true;
        s.gridDirty = false;
      }

      // --- Update player mesh position ---
      playerMesh.position.x = s.player.x * CELL_SIZE + CELL_SIZE * 0.5;
      playerMesh.position.z = s.player.y * CELL_SIZE + CELL_SIZE * 0.5;
      glowMesh.position.x = playerMesh.position.x;
      glowMesh.position.z = playerMesh.position.z;
      // Pulse the glow
      glowMat.opacity = 0.2 + Math.sin(s.time * 4) * 0.1;

      // --- Update Styxxer enemy mesh position ---
      styxxerGroup.position.x = en.x * CELL_SIZE;
      styxxerGroup.position.z = en.y * CELL_SIZE;
      animateStyxxer(s.time);

      // --- Update Sparx mesh positions (show active, hide unused) ---
      for (let si = 0; si < sparxMeshes.length; si++) {
        const sm = sparxMeshes[si];
        if (si < s.sparxList.length) {
          const sparx = s.sparxList[si];
          sm.mesh.visible = true;
          sm.glow.visible = true;
          sm.mesh.position.x = sparx.x * CELL_SIZE + CELL_SIZE * 0.5;
          sm.mesh.position.z = sparx.y * CELL_SIZE + CELL_SIZE * 0.5;
          sm.glow.position.x = sm.mesh.position.x;
          sm.glow.position.z = sm.mesh.position.z;
          // Pulse the sparx glow effect
          sm.glowMat.opacity = 0.25 + Math.sin(s.time * 8 + si * 3) * 0.15;
        } else {
          // Hide meshes for sparx that don't exist at this level
          sm.mesh.visible = false;
          sm.glow.visible = false;
        }
      }

      // --- Player invincibility flashing effect ---
      if (s.invincibleTimer > 0) {
        playerMat.transparent = true;
        playerMat.opacity = Math.sin(s.time * 15) > 0 ? 1 : 0.2;
      } else {
        playerMat.transparent = false;
        playerMat.opacity = 1;
      }

      // --- Adjust camera to fit the grid ---
      const camAspect = renderer.domElement.clientWidth / renderer.domElement.clientHeight;
      const gridAspect = (GRID_W * CELL_SIZE) / (GRID_H * CELL_SIZE);
      let camH, camW;
      if (camAspect > gridAspect) {
        // Window is wider than grid: fit height
        camH = GRID_H * CELL_SIZE * 1.05;
        camW = camH * camAspect;
      } else {
        // Window is taller than grid: fit width
        camW = GRID_W * CELL_SIZE * 1.05;
        camH = camW / camAspect;
      }
      camera.left = -camW / 2;
      camera.right = camW / 2;
      camera.top = camH / 2;
      camera.bottom = -camH / 2;
      camera.updateProjectionMatrix();

      // Render the scene
      renderer.render(scene, camera);
    };

    // Start the game loop
    loop();

    /**
     * Handle window resize events to keep the renderer and camera in sync with the viewport.
     */
    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      renderer.setSize(w, h);
      // Camera projection will be recalculated each frame in the loop
    };
    window.addEventListener("resize", handleResize);

    /**
     * Cleanup function: removes event listeners, cancels animation frame,
     * disposes renderer, and removes the canvas from the DOM.
     */
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("resize", handleResize);
      // Clear all key states to prevent stale keys from carrying over to the next level.
      // Without this, if a key was held when the level ended, the keyup event would be
      // missed (since the listener is removed above) and the new level would start with
      // the player moving in that direction.
      keysRef.current = {};
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [gameState, initLevel]);

  /**
   * Handles the "Start Game" action from the menu screen.
   * Resets all game state and transitions to 'playing' state.
   */
  const handleStart = useCallback(() => {
    stateRef.current = null;
    setLives(3);
    setLevel(1);
    setCapturedPercent(0);
    // Init the first level
    initLevel(1);
    if (stateRef.current) stateRef.current.lives = 3;
    setGameState("playing");
  }, [initLevel]);

  /**
   * Handles the "Next Level" action from the level complete screen.
   * Increments level, resets the grid but preserves lives.
   */
  const handleNextLevel = useCallback(() => {
    const nextLevel = (stateRef.current?.levelNum || 1) + 1;
    const currentLives = stateRef.current?.lives ?? 3;
    setLevel(nextLevel);
    initLevel(nextLevel);
    if (stateRef.current) stateRef.current.lives = currentLives;
    setGameState("playing");
  }, [initLevel]);

  /**
   * Handles the "Play Again" action from game over or won screens.
   * Fully resets the game back to level 1 with 3 lives.
   */
  const handleRestart = useCallback(() => {
    stateRef.current = null;
    setLives(3);
    setLevel(1);
    setCapturedPercent(0);
    initLevel(1);
    if (stateRef.current) stateRef.current.lives = 3;
    setGameState("playing");
  }, [initLevel]);

  /**
   * Effect to handle Enter key press on menu, level complete, game over, and won screens.
   * Wires the appropriate action to the Enter key for each game state.
   */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === "Enter" || e.code === "Space") {
        if (gameState === "menu") handleStart();
        else if (gameState === "levelComplete") handleNextLevel();
        else if (gameState === "gameOver" || gameState === "won") handleRestart();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [gameState, handleStart, handleNextLevel, handleRestart]);

  /**
   * Effect to manage the background soundtrack Audio element.
   * Creates the Audio on mount, plays/pauses based on gameState, and syncs mute state.
   * Cleans up (pauses and releases) on component unmount.
   */
  useEffect(() => {
    // Lazily create the Audio element on first mount
    if (!audioRef.current) {
      const audio = new Audio(soundtrackSrc);
      audio.loop = true;
      audio.volume = 0.35;
      audioRef.current = audio;
    }
    const audio = audioRef.current;
    audio.muted = isMuted;

    // Play the soundtrack while the game is actively being played
    if (gameState === "playing") {
      audio.play().catch(() => {
        // Browser may block autoplay; soundtrack will start on next user interaction
      });
    } else {
      // Pause the soundtrack on menu, game over, level complete, and won screens
      audio.pause();
    }

    // Cleanup: pause audio when the component unmounts
    return () => {
      audio.pause();
    };
  }, [gameState, isMuted]);

  // --- HUD Overlay ---
  // Top bar showing game title, lives, level, and capture percentage
  const hud = gameState === "playing" ? (
    <div style={{
      position: "absolute", top: 0, left: 0, right: 0,
      padding: "8px 16px",
      background: "linear-gradient(90deg, rgba(10,10,42,0.95), rgba(0,40,60,0.9), rgba(10,10,42,0.95))",
      borderBottom: "2px solid #00aaaa",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      zIndex: 10, boxSizing: "border-box",
      fontFamily: "'Courier New', monospace",
    }}>
      <div style={{
        color: "#00ffff", fontSize: "18px", fontWeight: "bold",
        letterSpacing: "6px", textShadow: "0 0 15px #00cccc, 0 0 30px #008888",
      }}>
        STYXXER
      </div>
      <div style={{ display: "flex", gap: "18px", alignItems: "center", color: "#aaddff", fontSize: "13px", flexWrap: "wrap" }}>
        <span>
          {"❤️".repeat(Math.max(0, lives))}
        </span>
        <span>LVL: <span style={{ color: "#88ff88" }}>{level}</span></span>
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span>{capturedPercent}%</span>
          <div style={{
            width: 60, height: 8, background: "rgba(0,50,50,0.8)",
            border: "1px solid #008888", borderRadius: 4, overflow: "hidden",
          }}>
            <div style={{
              width: `${Math.min(100, (capturedPercent / WIN_PERCENT) * 100)}%`,
              height: "100%",
              background: capturedPercent >= WIN_PERCENT
                ? "linear-gradient(90deg, #00ff88, #88ffaa)"
                : "linear-gradient(90deg, #00cccc, #00ffff)",
              transition: "width 0.3s",
            }} />
          </div>
        </span>
        {/* Mute/unmute toggle button for the background soundtrack */}
        <button
          onClick={() => setIsMuted((prev) => !prev)}
          title={isMuted ? "Unmute soundtrack" : "Mute soundtrack"}
          style={{
            background: "transparent",
            border: "1px solid #006666",
            borderRadius: 4,
            color: isMuted ? "#555555" : "#00ffcc",
            fontSize: 16,
            cursor: "pointer",
            padding: "2px 7px",
            lineHeight: 1,
            fontFamily: "inherit",
            WebkitTapHighlightColor: "transparent",
            boxShadow: isMuted ? "none" : "0 0 8px #00888844",
            transition: "color 0.2s, box-shadow 0.2s",
          }}
        >
          {isMuted ? "🔇" : "🔊"}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div style={{
      position: "relative", width: "100vw", height: "100svh", overflow: "hidden",
      fontFamily: "'Courier New', monospace", background: "#0a0a2a",
      touchAction: "none",
    }}>
      {/* Three.js canvas container — offset top to avoid being covered by the HUD bar */}
      <div ref={mountRef} style={{ position: "absolute", top: "48px", left: 0, right: 0, bottom: 0 }} />

      {/* HUD overlay */}
      {hud}

      {/* Mobile D-pad controls */}
      {showControls && gameState === "playing" && (
        <div style={{
          position: "absolute",
          bottom: 24,
          right: 24,
          display: "grid",
          gridTemplateAreas: `". up ." "left center right" ". down ."`,
          gridTemplateColumns: "58px 58px 58px",
          gridTemplateRows: "58px 58px 58px",
          gap: 5,
          zIndex: 15,
          pointerEvents: "auto",
        }}>
          <div style={{ gridArea: "up" }}>
            <DPadButton label="▲" keyCode="ArrowUp" keysRef={keysRef} />
          </div>
          <div style={{ gridArea: "left" }}>
            <DPadButton label="◀" keyCode="ArrowLeft" keysRef={keysRef} />
          </div>
          <div style={{ gridArea: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{
              width: 58, height: 58, borderRadius: 10,
              background: "rgba(0, 30, 60, 0.7)",
              border: "2px solid #006666",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#00aaaa", boxShadow: "0 0 8px #00cccc" }} />
            </div>
          </div>
          <div style={{ gridArea: "right" }}>
            <DPadButton label="▶" keyCode="ArrowRight" keysRef={keysRef} />
          </div>
          <div style={{ gridArea: "down" }}>
            <DPadButton label="▼" keyCode="ArrowDown" keysRef={keysRef} />
          </div>
        </div>
      )}

      {/* MENU screen */}
      {gameState === "menu" && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(5,5,20,0.95)", zIndex: 20,
        }}>
          <div style={{
            border: "2px solid #00aaaa", background: "rgba(5,15,30,0.98)",
            padding: "36px 44px", textAlign: "center", maxWidth: 420,
            boxShadow: "0 0 60px #00888866", margin: "0 16px", borderRadius: 4,
          }}>
            <div style={{
              color: "#00ffff", fontSize: "36px", fontWeight: "bold",
              letterSpacing: 8, marginBottom: 8,
              textShadow: "0 0 20px #00cccc, 0 0 40px #008888, 0 0 60px #006666",
            }}>
              STYXXER
            </div>
            <div style={{ color: "#006688", fontSize: 12, marginBottom: 24, letterSpacing: 4 }}>
              ◆ QIX-STYLE TERRITORY CAPTURE ◆
            </div>
            <div style={{ color: "#aabbcc", fontSize: 12, lineHeight: "2", marginBottom: 24, textAlign: "left" }}>
              🟢 You are the <span style={{ color: "#00ff66" }}>green marker</span>.<br />
              ✏️ Move into empty area to <span style={{ color: "#ffcc00" }}>draw a trail</span>.<br />
              🔗 Connect trail back to captured territory to <span style={{ color: "#00cccc" }}>claim it</span>.<br />
              🎯 Capture <span style={{ color: "#ffdd00" }}>{WIN_PERCENT}%</span> of the field to advance!<br />
              ⚠️ Avoid the <span style={{ color: "#ff00ff" }}>Styxxer</span> — it destroys your trail!<br />
              ⚡ Watch out for <span style={{ color: "#ff3300" }}>Sparx</span> — they patrol the borders!<br />
              <br />
              <span style={{ color: "#557788" }}>📱 D-pad · ⌨️ Arrow keys / WASD</span>
            </div>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 12, color: "#88ddff", fontSize: 12, marginBottom: 20, letterSpacing: 1,
              background: "rgba(0,20,40,0.6)", border: "1px solid #005566", padding: "8px 12px",
            }}>
              <span>Show on-screen controls</span>
              <button
                onClick={() => setShowControls((prev) => !prev)}
                style={{
                  background: showControls ? "linear-gradient(135deg, #006666, #009999)" : "#223344",
                  color: "#ffffff", border: "1px solid #00aaaa",
                  padding: "6px 14px", fontSize: 11, letterSpacing: 1.5,
                  cursor: "pointer", fontFamily: "inherit", boxShadow: "0 0 12px #00666644",
                  borderRadius: 4, WebkitTapHighlightColor: "transparent", minWidth: 70,
                }}
              >
                {showControls ? "ON" : "OFF"}
              </button>
            </div>
            {/* Mute/unmute toggle for the soundtrack on the menu screen */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 12, color: "#88ddff", fontSize: 12, marginBottom: 20, letterSpacing: 1,
              background: "rgba(0,20,40,0.6)", border: "1px solid #005566", padding: "8px 12px",
            }}>
              <span>🎵 Soundtrack</span>
              <button
                onClick={() => setIsMuted((prev) => !prev)}
                style={{
                  background: !isMuted ? "linear-gradient(135deg, #006666, #009999)" : "#223344",
                  color: "#ffffff", border: "1px solid #00aaaa",
                  padding: "6px 14px", fontSize: 11, letterSpacing: 1.5,
                  cursor: "pointer", fontFamily: "inherit", boxShadow: "0 0 12px #00666644",
                  borderRadius: 4, WebkitTapHighlightColor: "transparent", minWidth: 70,
                }}
              >
                {isMuted ? "OFF" : "ON"}
              </button>
            </div>
            <button
              onClick={handleStart}
              style={{
                background: "linear-gradient(135deg, #004444, #008888)",
                color: "#ffffff", border: "1px solid #00cccc",
                padding: "14px 44px", fontSize: "14px", letterSpacing: "4px",
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 0 20px #00888866", borderRadius: 4,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              START GAME
            </button>
            <div style={{ color: "#446666", fontSize: 11, marginTop: 12, letterSpacing: 2 }}>
              Press ENTER to start
            </div>
          </div>
        </div>
      )}

      {/* LEVEL COMPLETE screen */}
      {gameState === "levelComplete" && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(0,10,5,0.85)", zIndex: 25,
        }}>
          <div style={{
            border: "2px solid #22aa55", background: "rgba(5,20,10,0.97)",
            padding: "36px 48px", textAlign: "center",
            boxShadow: "0 0 60px #00aa4499", borderRadius: 4,
          }}>
            <div style={{
              color: "#44ff88", fontSize: "28px", fontWeight: "bold",
              letterSpacing: 3, marginBottom: 10,
              textShadow: "0 0 20px #00ff66",
            }}>
              🏅 LEVEL {level} COMPLETE!
            </div>
            <div style={{ color: "#88ddaa", fontSize: 14, marginBottom: 8, letterSpacing: 1 }}>
              Territory captured: {capturedPercent}%
            </div>
            <div style={{ color: "#44aa66", fontSize: 12, marginBottom: 28, letterSpacing: 2 }}>
              Get ready for level {level + 1}...
            </div>
            <button
              onClick={handleNextLevel}
              style={{
                background: "linear-gradient(135deg, #115522, #228844)",
                color: "#ffffff", border: "1px solid #44ff88",
                padding: "13px 44px", fontSize: "14px", letterSpacing: "4px",
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 0 20px #00aa4488", borderRadius: 4,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              NEXT LEVEL
            </button>
            <div style={{ color: "#446644", fontSize: 11, marginTop: 10, letterSpacing: 2 }}>
              Press ENTER to continue
            </div>
          </div>
        </div>
      )}

      {/* GAME OVER screen */}
      {gameState === "gameOver" && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(10,0,0,0.85)", zIndex: 25,
        }}>
          <div style={{
            border: "2px solid #aa2222", background: "rgba(20,5,5,0.97)",
            padding: "36px 48px", textAlign: "center",
            boxShadow: "0 0 60px #880000cc", borderRadius: 4,
          }}>
            <div style={{
              color: "#ff3333", fontSize: "32px", fontWeight: "bold",
              letterSpacing: 4, marginBottom: 10,
              textShadow: "0 0 20px #ff0000",
            }}>
              💀 GAME OVER
            </div>
            <div style={{ color: "#aa5555", fontSize: 14, marginBottom: 8 }}>
              You reached level {level}
            </div>
            <div style={{ color: "#885555", fontSize: 13, marginBottom: 28 }}>
              Territory captured: {capturedPercent}%
            </div>
            <button
              onClick={handleRestart}
              style={{
                background: "linear-gradient(135deg, #881111, #cc2222)",
                color: "#ffffff", border: "1px solid #ff4444",
                padding: "13px 44px", fontSize: "14px", letterSpacing: "4px",
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 0 20px #aa000088", borderRadius: 4,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              PLAY AGAIN
            </button>
            <div style={{ color: "#664444", fontSize: 11, marginTop: 10, letterSpacing: 2 }}>
              Press ENTER to restart
            </div>
          </div>
        </div>
      )}

      {/* YOU'RE DEAD overlay — shown briefly after each life lost */}
      {showDeathOverlay && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(80,0,0,0.65)",
          zIndex: 30,
          pointerEvents: "none",
          animation: "none",
        }}>
          <div style={{
            color: "#ff2222",
            fontSize: "clamp(36px, 10vw, 88px)",
            fontWeight: "bold",
            fontFamily: "'Courier New', monospace",
            letterSpacing: "6px",
            textShadow: "0 0 30px #ff0000, 0 0 60px #ff0000, 0 0 100px #880000",
            userSelect: "none",
            textAlign: "center",
          }}>
            YOU&apos;RE DEAD!
          </div>
        </div>
      )}

      {/* WON screen */}
      {gameState === "won" && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(5,5,20,0.9)", zIndex: 25,
        }}>
          <div style={{
            border: "2px solid #ffdd00", background: "rgba(15,15,5,0.97)",
            padding: "36px 48px", textAlign: "center",
            boxShadow: "0 0 60px #ffaa0066", borderRadius: 4,
          }}>
            <div style={{
              color: "#ffdd00", fontSize: "30px", fontWeight: "bold",
              letterSpacing: 4, marginBottom: 10,
              textShadow: "0 0 20px #ffaa00, 0 0 40px #ff8800",
            }}>
              🏆 YOU WON!
            </div>
            <div style={{ color: "#ddaa44", fontSize: 14, marginBottom: 28 }}>
              All {MAX_LEVEL} levels completed!
            </div>
            <button
              onClick={handleRestart}
              style={{
                background: "linear-gradient(135deg, #886600, #ccaa00)",
                color: "#ffffff", border: "1px solid #ffdd00",
                padding: "13px 44px", fontSize: "14px", letterSpacing: "4px",
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 0 20px #ffaa0066", borderRadius: 4,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              PLAY AGAIN
            </button>
            <div style={{ color: "#665500", fontSize: 11, marginTop: 10, letterSpacing: 2 }}>
              Press ENTER to restart
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
